import { KatFileUpload } from '@amzn/katal-react/es/file-upload';
import React, { useContext, useState } from 'react';
import Uploader, { isValidFileType } from 'src/components/FileUpload/Uploader';

import { createCaseSlice } from 'src/state/slice/createCaseSlice';

import { useAppDispatch, useAppSelector } from 'src/state/store/hooks';
import {
  CreateCaseConstants,
  UploaderConstants,
} from 'src/constants/ProjectConstants';
import Spacer from 'src/components/Layout/Spacer';
import { convertFileToDocumentDetails as convert } from 'src/state/slice/common/FileUploadReducers';
import DmsFileUploadClient from 'src/integration/dms/DmsFileUploadClient';
import { IDmsFile } from 'src/integration/dms/DmsModel';
import {
  CONFIG_TYPE,
  MAX_NUMBER_OF_ATTACHMENTS,
  MAX_TOTAL_ATTACHMENTS_SIZE,
  UPLOAD_MAX_FILE_SIZE,
} from 'src/constants/GlobalConstants';
import { logger } from 'src/logger';
import { AuthContext } from 'src/state/providers/AuthProvider';
import { emitClickMetrics, MetricsName } from 'src/metrics';
import { Header } from 'src/react-jsonschema-katal';
import {
  FileAttributes,
  convertFileToFileAttributesAction as toFileAttributes,
} from 'src/state/slice/common/FileUploadStatusReducers';
import { TranslationContext } from 'src/state/providers/TranslationProvider';
import { PageContext } from 'src/state/providers/PageProvider';
import { getAllowedFileTypes } from 'src/components/Correspondence/TextEditor';
import { fetchFromAppConfig } from 'src/config-store/ConfigFetcher';
import { validateFileAttachments } from 'src/utilities/AttachmentValidationUtils';

const CreateCaseAttachments = () => {
  const { preferredAccountId, clientId, customerId } = useContext(AuthContext);
  const { pageScope } = useContext(PageContext);
  const { fileUploadStatus, attachmentValidationError } = useAppSelector(
    (state) => state.createCase,
  );
  const dispatch = useAppDispatch();
  const allowedFileTypes = getAllowedFileTypes(preferredAccountId, clientId);

  // Local state to track all files user has attempted to attach (for validation purposes)
  const [attemptedFiles, setAttemptedFiles] = useState<File[]>([]);

  const totalAttachmentsSize = fetchFromAppConfig(
    preferredAccountId,
    clientId,
    CONFIG_TYPE.PROPERTIES,
    MAX_TOTAL_ATTACHMENTS_SIZE,
  );

  const maxNumberOfAttachments = fetchFromAppConfig(
    preferredAccountId,
    clientId,
    CONFIG_TYPE.PROPERTIES,
    MAX_NUMBER_OF_ATTACHMENTS,
  );

  const {
    addAttachment,
    removeAttachment,
    replaceAttachment,
    addFileToUploadTracker,
    setFileToUploadComplete,
    removeFileFromUploadTracker,
    setFileToError,
    setAttachmentValidationError,
    clearAttachmentValidationError,
  } = createCaseSlice.actions;

  const { fileUploaderBundle } = useContext(TranslationContext);
  const errorSummary: string = fileUploaderBundle.getMessage(
    UploaderConstants.ERROR_GENERIC_MESSAGE_FOR_UI,
  );

  return (
    <>
      <Header
        title={fileUploaderBundle.getMessage(
          CreateCaseConstants.ADD_ATTACHMENTS,
        )}
      />
      <Uploader
        fileStates={fileUploadStatus.fileUploadTracker}
        onFilesAttached={(e: KatFileUpload.FilesAttachedEvent) => {
          // Clear any existing validation error
          dispatch(clearAttachmentValidationError());

          const newFiles = Array.from(e.detail.files);

          // Add new files to attempted files list for validation
          const updatedAttemptedFiles = [...attemptedFiles, ...newFiles];
          setAttemptedFiles(updatedAttemptedFiles);

          const validationResult = validateFileAttachments(
            updatedAttemptedFiles,
            maxNumberOfAttachments,
            totalAttachmentsSize,
          );

          if (validationResult.error) {
            const errorMessage = fileUploaderBundle.formatMessage(
              validationResult.error.messageId,
              validationResult.error.messageParams,
            );
            logger.error(
              `File attachment validation failed for user ${customerId}: ${errorMessage}`,
            );
            // Set validation error instead of individual file errors
            dispatch(setAttachmentValidationError(errorMessage));
          }

          e.detail.files.forEach((file) => {
            const fileAttribute: FileAttributes = toFileAttributes(
              file,
              errorSummary,
            );
            if (file.size >= UPLOAD_MAX_FILE_SIZE) {
              logger.error(
                `Size exceeded while uploading: ${file.name} by user ${customerId}. File Size ${file.size}bytes`,
              );
              dispatch(setFileToError(fileAttribute));
              return;
            }
            const fileExtension: string | undefined = file.name.includes('.')
              ? file.name.split('.').pop()
              : undefined;
            if (!isValidFileType(fileExtension, allowedFileTypes)) {
              logger.error(
                `${customerId} uploading an invalid file type: ${file.name}`,
              );
              dispatch(setFileToError(fileAttribute));
              return;
            }
            dispatch(addFileToUploadTracker(fileAttribute));
            const fileUploadClient = new DmsFileUploadClient(file, pageScope);
            fileUploadClient
              .orchestrateFileUpload()
              .then((dmsFile: IDmsFile) => {
                dispatch(setFileToUploadComplete(fileAttribute));
                logger.info(
                  `File: ${file.name} uploaded successfully by user ${customerId}. DMS Id: ${dmsFile.documentId}`,
                );
                return dmsFile;
              })
              .then((dmsFile: IDmsFile) => {
                dispatch(addAttachment(convert(dmsFile.documentId, file)));
                emitClickMetrics(
                  MetricsName.CREATE_CASE_PAGE,
                  'DocumentUploaded',
                );
              })
              .catch((error: Error) => {
                logger.error(
                  `Error occurred while uploading file: ${file.name} by user ${customerId}.  Check reason in context`,
                  error,
                );
                dispatch(setFileToError(fileAttribute));
              });
          });
        }}
        onFilesRemoved={(e: KatFileUpload.FilesRemovedEvent) => {
          const removedFiles = Array.from(e.detail.files);

          // Remove files from local attempted files state
          const updatedAttemptedFiles = attemptedFiles.filter(
            (attemptedFile) =>
              !removedFiles.some(
                (removedFile) => removedFile.name === attemptedFile.name,
              ),
          );
          setAttemptedFiles(updatedAttemptedFiles);

          removedFiles.forEach((file) => {
            //Id is not being compared in removing the file. Name is used instead. Hence we can use the name as the Id.
            dispatch(removeAttachment(convert(file.name, file)));
            dispatch(removeFileFromUploadTracker(toFileAttributes(file)));
            emitClickMetrics(MetricsName.CREATE_CASE_PAGE, 'DocumentDeleted');
            logger.info(
              `File: ${file.name} removed successfully by user ${customerId}`,
            );
          });

          // Re-validate the current state using updated local state
          const validationResult = validateFileAttachments(
            updatedAttemptedFiles, // Updated attempted files after removal
            maxNumberOfAttachments,
            totalAttachmentsSize,
          );

          // Update validation error based on current state
          if (validationResult.isValid) {
            dispatch(clearAttachmentValidationError());
            logger.info(
              `Cleared validation error after file removal by user: ${customerId}`,
            );
          } else if (validationResult.error) {
            // Update validation error to reflect current state after removal
            const errorMessage = fileUploaderBundle.formatMessage(
              validationResult.error.messageId,
              validationResult.error.messageParams,
            );
            dispatch(setAttachmentValidationError(errorMessage));
            logger.info(
              `Updated validation error after file removal by user: ${customerId}. New error: ${errorMessage}`,
            );
          }
        }}
        onFilesReplaced={(e: KatFileUpload.FilesReplacedEvent) => {
          // Clear any existing validation error
          dispatch(clearAttachmentValidationError());

          const newFiles = Array.from(e.detail.newFiles);
          const oldFiles = Array.from(e.detail.oldFiles);

          // Update local attempted files state - replace old files with new files
          const oldFileNames = oldFiles.map((file) => file.name);
          const updatedAttemptedFiles = [
            ...attemptedFiles.filter(
              (file) => !oldFileNames.includes(file.name),
            ),
            ...newFiles,
          ];
          setAttemptedFiles(updatedAttemptedFiles);

          const validationResult = validateFileAttachments(
            updatedAttemptedFiles,
            maxNumberOfAttachments,
            totalAttachmentsSize,
          );

          if (validationResult.error) {
            const errorMessage = fileUploaderBundle.formatMessage(
              validationResult.error.messageId,
              validationResult.error.messageParams,
            );
            logger.error(
              `File replacement validation failed for user ${customerId}: ${errorMessage}`,
            );
            dispatch(setAttachmentValidationError(errorMessage));
          }

          e.detail.newFiles.forEach((newFile, index) => {
            const oldFile = e.detail.oldFiles[index];
            const oldFileAttribute: FileAttributes = toFileAttributes(
              oldFile,
              errorSummary,
            );
            const newFileAttribute: FileAttributes = toFileAttributes(newFile);
            if (newFile.size >= UPLOAD_MAX_FILE_SIZE) {
              dispatch(setFileToError(oldFileAttribute));
              logger.error(
                `Size exceeded while uploading: ${newFile.name} by user ${customerId}. File Size ${newFile.size}bytes`,
              );
              return;
            }
            dispatch(addFileToUploadTracker(newFileAttribute));
            const fileUploadClient = new DmsFileUploadClient(
              newFile,
              pageScope,
            );
            fileUploadClient
              .orchestrateFileUpload()
              .then((dmsNewFile: IDmsFile) => {
                dispatch(setFileToUploadComplete(newFileAttribute));
                logger.info(
                  `File: ${newFile.name} uploaded successfully by user ${customerId}`,
                );
                return dmsNewFile;
              })
              .then((dmsNewFile: IDmsFile) => {
                dispatch(
                  replaceAttachment({
                    newFile: convert(dmsNewFile.documentId, newFile),
                    //Id is not being compared in removing the file. Name is used instead. Hence we can use the name as the Id.
                    oldFile: convert(oldFile.name, oldFile),
                  }),
                );
                logger.info(
                  `File: ${newFile.name} saved successfully by user ${customerId} to state`,
                );
              })
              .catch((error: Error) => {
                logger.error(
                  `Error occured while uploading file: ${newFile.name} by user ${customerId}.  Check reason in context`,
                  error,
                );
                dispatch(setFileToError(oldFileAttribute));
              });
          });
        }}
        allowedFiles={allowedFileTypes}
        errorMessage={attachmentValidationError || undefined}
      />
      <Spacer height="1rem" />
    </>
  );
};

export default CreateCaseAttachments;
